import { useCallback, useEffect, useRef } from 'react'

interface IUseMonitor {
  timeout: number
  sessionExpiry: number
  isEditingQuiz: boolean
  onTimeOut: () => void
  onSessionExpiry?: () => void
  onSessionExpiryClose?: () => void
}

const events = ['load', 'mousemove', 'mousedown', 'click', 'scroll', 'keypress']

const useMonitor = ({
  timeout,
  sessionExpiry,
  isEditingQuiz,
  onTimeOut,
  onSessionExpiry,
  onSessionExpiryClose
}: IUseMonitor) => {
  const sessionExpiryTimerRef = useRef<NodeJS.Timeout | null>(null)
  const sessionExpiryCloseTimerRef = useRef<NodeJS.Timeout | null>(null)
  const timeoutTimerRef = useRef<NodeJS.Timeout | null>(null)

  const hasUnsavedChanges = sessionExpiry && isEditingQuiz

  const handleSessionExpiry = useCallback(() => {
    if (onSessionExpiry && hasUnsavedChanges) {
      console.log('Calling session expiry handler')
      onSessionExpiry()
    }
  }, [onSessionExpiry, hasUnsavedChanges])

  const handleSessionExpiryClose = useCallback(() => {
    if (onSessionExpiryClose) {
      console.log('Calling session expiry close handler')
      onSessionExpiryClose()
    }
  }, [onSessionExpiryClose])

  const handleTimeout = useCallback(() => {
    if (onTimeOut) {
      console.log('Calling timeout handler')
      onTimeOut()
    }
  }, [onTimeOut])

  useEffect(() => {
    const startTime = Date.now()

    const clearAllTimeouts = () => {
      if (sessionExpiryTimerRef.current) {
        clearTimeout(sessionExpiryTimerRef.current)
        sessionExpiryTimerRef.current = null
      }

      if (sessionExpiryCloseTimerRef.current) {
        clearTimeout(sessionExpiryCloseTimerRef.current)
        sessionExpiryCloseTimerRef.current = null
      }

      if (timeoutTimerRef.current) {
        clearTimeout(timeoutTimerRef.current)
        timeoutTimerRef.current = null
      }
    }

    const setTimeouts = () => {
      clearAllTimeouts()
      const currentTime = Date.now()
      const elapsedTime = currentTime - startTime

      if (hasUnsavedChanges && sessionExpiry) {
        const remainingTimeToExpiry = Math.max(0, sessionExpiry - elapsedTime)
        sessionExpiryTimerRef.current = setTimeout(handleSessionExpiry, remainingTimeToExpiry)

        // Calculate relative to the configured timeout
        const closingTime = timeout - 60000 // 1 minute before timeout
        const remainingTimeToClose = Math.max(0, closingTime - elapsedTime)
        sessionExpiryCloseTimerRef.current = setTimeout(handleSessionExpiryClose, remainingTimeToClose)
      }

      const remainingTimeToTimeout = Math.max(0, timeout - elapsedTime)
      timeoutTimerRef.current = setTimeout(handleTimeout, remainingTimeToTimeout)
    }

    const refreshTimeout = () => {
      clearAllTimeouts()
      setTimeouts()
    }

    events.forEach((event) => {
      window.addEventListener(event, refreshTimeout)
    })

    setTimeouts()

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, refreshTimeout)
      })
      clearAllTimeouts()
    }
  }, [
    handleSessionExpiry,
    handleSessionExpiryClose,
    handleTimeout,
    timeout,
    sessionExpiry,
    isEditingQuiz,
    hasUnsavedChanges
  ])

  return null
}

export default useMonitor
